import {api, HTTP_STATUS} from '@nfq/typed-next-api';

import type {HTTP_METHODS, MutationRepoArgs} from '@nfq/typed-next-api';
import type {trackLinkClick} from 'ApiRoutes/tracking/click';
import type {trackpageView} from 'ApiRoutes/tracking/view';
import type {TrackingAdapter} from 'Domain/adapters/TrackingService';
import type {PageViewTrackingBody} from 'Domain/entities/tracking';
import type {LinkClickTrackingBody} from 'GlobalShared/types/trackingBody';

/**
 * The `TrackingService` class implements the `TrackingAdapter` interface and is responsible for handling tracking-related operations.
 * This includes tracking user interactions such as link clicks and page views by sending relevant data to a backend API.
 * Each method returns an HTTP status code to indicate the success or failure of the tracking operation.
 */
class TrackingService implements TrackingAdapter {
    /**
     * Tracks when a user clicks a link by sending data about the link click event to the backend API.
     * This method captures details such as whether the link is external and the URL that was clicked.
     * The information is sent via an HTTP `POST` request, and the response indicates whether the tracking operation was successful.
     *
     * @param key              A unique identifier or key used for the cache. This key may be used to distinguish between different caches.
     * @param props            The properties object containing the request arguments.
     * @param props.arg        An object containing tracking parameters, including the request body and the HTTP method used to send the data.
     * @param props.arg.body   An object representing the data related to the link click event. It includes the following properties: - `external`: A boolean that indicates whether the link is external (e.g., links to a different domain). - `url`: A string representing the URL of the link that was clicked.
     * @param props.arg.method The HTTP method used for the request, which should be `POST` for tracking link clicks.
     * @returns A Promise that resolves to an object containing an HTTP status code:
     * - `HTTP_STATUS.OK` if the tracking was successful.
     * - `HTTP_STATUS.BAD_REQUEST` if the tracking operation failed (e.g., due to an invalid request or network error).
     *
     * @example
     * ```ts
     * const trackingService = new TrackingService();
     * const response = await trackingService.trackLinkClick('key', {
     *   arg: {
     *     body: { external: true, url: 'https://example.com' },
     *     method: 'POST'
     *   }
     * });
     * response.status // Outputs: HTTP_STATUS.OK if successful
     * ```
     */
    async trackLinkClick(
        key: string, {arg: {body, method}}: MutationRepoArgs<LinkClickTrackingBody, HTTP_METHODS.POST>
    ) {
        try {
            await api<typeof trackLinkClick>('/api/tracking/click', {
                body: {
                    external: body.external,
                    url: body.url
                },
                method
            });

            return {status: HTTP_STATUS.OK};
        } catch (error) {
            return {status: HTTP_STATUS.BAD_REQUEST};
        }
    }

    /**
     * Tracks when a user views a page by sending data about the page view event to the backend API.
     * This method captures details such as whether it is the first time the page is viewed, the URL of the page, the referrer, the referring media (e.g., social media or ads), and the viewport dimensions.
     * The information is sent via an HTTP `POST` request, and the response indicates whether the tracking operation was successful.
     *
     * @param key              A unique identifier or key used for the cache. This key may be used to distinguish between different caches.
     * @param props            The properties object containing the request arguments.
     * @param props.arg        An object containing tracking parameters, including the request body and the HTTP method used to send the data.
     * @param props.arg.body   An object representing the data related to the page view event. It includes the following properties: - `firstView`: A boolean that indicates whether this is the user's first view of the page. - `referrer`: A string representing the URL of the page that referred the user to the current page (i.e., the previous page). - `referringMedia`: A string indicating the media through which the user was referred to the page (e.g., "social", "ads"). - `url`: A string representing the URL of the current page being viewed. - `viewport`: A string that captures the user's viewport dimensions (e.g., '1920x1080').
     * @param props.arg.method The HTTP method used for the request, which should be `POST` for tracking page views.
     * @returns A Promise that resolves to an object containing an HTTP status code:
     * - `HTTP_STATUS.OK` if the tracking was successful.
     * - `HTTP_STATUS.BAD_REQUEST` if the tracking operation failed (e.g., due to an invalid request or network error).
     *
     * @example
     * ```ts
     * const trackingService = new TrackingService();
     * const response = await trackingService.trackPageView('key', {
     *   arg: {
     *     body: { firstView: true, url: 'https://example.com' },
     *     method: 'POST'
     *   }
     * });
     * response.status // Outputs: HTTP_STATUS.OK if successful
     * ```
     */
    async trackPageView(
        key: string, {arg: {body, method}}: MutationRepoArgs<PageViewTrackingBody, HTTP_METHODS.POST>
    ) {
        try {
            await api<typeof trackpageView>('/api/tracking/view', {
                body: {
                    firstView: body.firstView,
                    referrer: body.referrer,
                    referringMedia: body.referringMedia,
                    url: body.url,
                    viewport: body.viewport
                },
                method
            });

            return {status: HTTP_STATUS.OK};
        } catch (error) {
            return {status: HTTP_STATUS.BAD_REQUEST};
        }
    }
}

export default new TrackingService();